'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useId, useRef, useState, useEffect, useMemo, Children, isValidElement, cloneElement } from 'react';
import { tokensRaw } from '../../../core/tokens/tokens-raw.js';
import { useSwanI18nContext } from '../../contexts/i18n/swan-i18n-provider.context.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { AdaptiveCarouselSlideNavigationInternal } from './adaptive-carousel-slide-navigation-internal.component.js';
import { AdaptiveCarouselSlidePickerInternal } from './adaptive-carousel-slide-picker-internal.component.js';
import { AdaptiveCarouselContext } from './adaptive-carousel.context.js';
import { useCustomArrowHeight, useCarouselPagination } from './adaptive-carousel.hooks.js';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.component.js';
import { prefersReducedMotion } from '../../../core/utilities/prefers-reduced-motion.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { RenderComp } from '../core/render-comp.component.js';

function _define_property(e, t, r) {
    return t in e ? Object.defineProperty(e, t, {
        value: r,
        enumerable: true,
        configurable: true,
        writable: true
    }) : e[t] = r, e;
}
function _object_spread(e) {
    for(var t = 1; t < arguments.length; t++){
        var r = null != arguments[t] ? arguments[t] : {}, o = Object.keys(r);
        "function" == typeof Object.getOwnPropertySymbols && (o = o.concat(Object.getOwnPropertySymbols(r).filter(function(e) {
            return Object.getOwnPropertyDescriptor(r, e).enumerable;
        }))), o.forEach(function(t) {
            _define_property(e, t, r[t]);
        });
    }
    return e;
}
function ownKeys(e, t) {
    var r = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
        var o = Object.getOwnPropertySymbols(e);
        r.push.apply(r, o);
    }
    return r;
}
function _object_spread_props(e, t) {
    return t = null != t ? t : {}, Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function(r) {
        Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    }), e;
}
function _object_without_properties(e, t) {
    if (null == e) return {};
    var r, o, a = _object_without_properties_loose(e, t);
    if (Object.getOwnPropertySymbols) {
        var i = Object.getOwnPropertySymbols(e);
        for(o = 0; o < i.length; o++)r = i[o], !(t.indexOf(r) >= 0) && Object.prototype.propertyIsEnumerable.call(e, r) && (a[r] = e[r]);
    }
    return a;
}
function _object_without_properties_loose(e, t) {
    if (null == e) return {};
    var r, o, a = {}, i = Object.keys(e);
    for(o = 0; o < i.length; o++)r = i[o], t.indexOf(r) >= 0 || (a[r] = e[r]);
    return a;
}
const AdaptiveCarousel = renderWithRef('AdaptiveCarousel', null, (f, h)=>{
    useComponentStylesLoaded('AdaptiveCarousel', SWAN_STYLE_KEY_MAP.adaptiveCarousel);
    let { children: S, onSlideChange: P, skin: x, slidePicker: k = 'standard', slidesToShow: T } = f, D = _object_without_properties(f, [
        "children",
        "onSlideChange",
        "skin",
        "slidePicker",
        "slidesToShow"
    ]), { getPluralKey: C, t: M } = useSwanI18nContext(), A = T;
    null == A && (A = 'promo-bar' === x || 'standard' !== k ? 1 : tokensRaw.SwanCompAdaptiveCarouselDefaultSlides);
    let I = useId(), N = useRef(null), E = useRef(null), L = useRef(null), [K, W] = useState(true), [z, R] = useState(false), $ = useCustomArrowHeight(N, [
        '.swan-base-tile-image',
        '.swan-standard-tile-image'
    ]), q = getMap(), B = q.size || 0, F = Array.from(q.values()), [G, H] = useState(0), { calculatedSlidesToShow: J, closestPage: Q, lastVisibleSlideIndex: U, pages: V, scrolledToEnd: X, slidesToScroll: Y, slidesToShowStyle: Z } = useCarouselPagination({
        derivedSlidesToShow: A,
        firstVisibleSlideIndex: G,
        totalSlides: B
    });
    function getMap() {
        return E.current || (E.current = new Map()), E.current;
    }
    useEffect(()=>{
        R(X);
    }, [
        X
    ]);
    let goToSlide = (e)=>{
        let t = F[Math.min(Math.max(e, 0), B - 1)];
        if (t) {
            var r;
            null === (r = N.current) || void 0 === r || r.scrollTo({
                left: t.offsetLeft,
                behavior: prefersReducedMotion() ? 'instant' : 'smooth'
            });
        }
    }, ee = useMemo(()=>M(C('adaptive-carousel.accessible-text-for-visible-slides', J), {
            start: G + 1,
            end: U + 1,
            totalSlides: B
        }), [
        J,
        G,
        C,
        U,
        M,
        B
    ]), et = new Set([
        'swan-adaptive-carousel'
    ]);
    'promo-bar' === x && et.add('swan-adaptive-carousel-promo-bar');
    let er = _object_spread_props(_object_spread({}, D), {
        'aria-roledescription': M('adaptive-carousel.aria-roledescription'),
        id: I,
        style: _object_spread({}, Z, $ && {
            '--swan-internal-adaptive-carousel-arrow-height': `${$}px`
        }, D.style)
    });
    return jsx(AdaptiveCarouselContext.Provider, {
        value: {
            firstVisibleSlideIndex: G,
            goToSlide,
            lastVisibleSlideIndex: U
        },
        children: jsxs(RenderComp, {
            root: "section",
            forwardedRef: h,
            classNames: et,
            props: er,
            children: [
                jsx(AdaptiveCarouselSlideNavigationInternal, {
                    carouselId: I,
                    isPreviousDisabled: K,
                    isNextDisabled: z,
                    setPreviousSlide: ()=>{
                        goToSlide(G - Y);
                    },
                    setNextSlide: ()=>{
                        goToSlide(G + Y);
                    },
                    skin: x
                }),
                jsx("div", {
                    ref: N,
                    className: "swan-adaptive-carousel-track",
                    onScroll: ()=>{
                        let e = N.current;
                        if (!e) return;
                        let t = e.scrollLeft, r = F.findIndex((e)=>e.offsetLeft + 30 >= t);
                        'number' == typeof r && (H(r), W(t <= 30), R(t + e.clientWidth + 30 >= e.scrollWidth), P && (L.current && clearTimeout(L.current), L.current = setTimeout(()=>{
                            P(r + 1, U + 1);
                        }, 200)));
                    },
                    tabIndex: 0,
                    "data-testid": "adaptive-carousel-track",
                    children: Children.map(S, (e, t)=>{
                        if (isValidElement(e)) return cloneElement(e, {
                            ref: (t)=>{
                                let r = getMap();
                                r.set(e, t), t || r.delete(e);
                            },
                            active: t >= G && t <= U,
                            'aria-label': M('adaptive-carousel.accessible-text-for-slide', {
                                current: t + 1,
                                totalSlides: B
                            })
                        });
                    })
                }),
                jsx(AdaptiveCarouselSlidePickerInternal, {
                    closestPage: Q,
                    goToSlide: goToSlide,
                    pages: V
                }),
                'standard' !== k ? k : void 0,
                jsx(VisuallyHidden, {
                    "aria-live": "polite",
                    "data-testid": "adaptive-carousel-live-region",
                    children: ee
                })
            ]
        })
    });
});

export { AdaptiveCarousel };
